import { useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import {
  Button,
  Form,
  FormDateTimeInput,
  FormField,
  FormSelect,
  HelperText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from '@/_components';
import { FormLayout } from '@/_containers';
import {
  EMAIL_TEMPLATE_DEFAULTS_QUERY_KEY,
  EMAIL_TEMPLATE_DEFAULTS_STALE_TIME,
  fetchEmailTemplateDefaults,
  getCachedEmailTemplateDefaults,
} from '@/_pages/settings/email-template-defaults/shared/apis';
import type { EmailTemplateDefaultModule } from '@/_pages/settings/email-template-defaults/shared/schemas';
import type { EmailTemplateQueryLinkerSchema } from '@/_pages/settings/email-templates/shared/schemas/email-templates-query-linker-schema';
import { usePermissions } from '@/_providers';
import { dateTime, nameResolver, queryClient, snackbar, stopPropagate } from '@/_shared';

import { InvoiceEmailRecipientsList } from './invoice-email-recipients-list';
import { InvoiceEmailTemplatesSelect } from './invoice-email-templates-select';

import { INVOICE_SCHEDULE_EMAIL_ENABLED, INVOICE_SEND_EMAIL_API_ENABLED, useSendInvoiceEmailMutation } from '../apis';
import { getInvoiceEmailContactIds, getInvoiceEmailRecipients } from '../get-invoice-email-recipients';
import type { InvoiceQuerySchema } from '../schemas/invoices/invoice-query-schema';
import {
  formatScheduledAtSummary,
  getDefaultScheduledAt,
  getInvoiceEmailActionOption,
  getInvoiceEmailActionOptions,
  getInvoiceEmailSendSuccessMessage,
  getScheduleMinDateTime,
  invoiceEmailSendFormSchema,
  type InvoiceEmailSendFormSchema,
} from '../schemas/send-invoice-email-mutation-schema';
import {
  fetchInvoiceEmailTemplates,
  getCachedInvoiceEmailTemplates,
  INVOICE_EMAIL_TEMPLATES_STALE_TIME,
  invoiceEmailTemplatesQueryKey,
  resolveInvoiceEmailDefaultTemplate,
} from '../use-get-invoice-email-templates-mutation';

const defaultFormValues = (): InvoiceEmailSendFormSchema => ({
  email_action: 'send',
  template: null,
  scheduled_at: null,
});

function isQueryCacheFresh(dataUpdatedAt: number | undefined, staleTimeMs: number): boolean {
  return dataUpdatedAt != null && Date.now() - dataUpdatedAt < staleTimeMs;
}

type InvoiceEmailSendModalProps = {
  open: boolean;
  onClose: () => void;
  invoice: InvoiceQuerySchema;
  emailTemplateDefaultModule: EmailTemplateDefaultModule;
  onSendStart: (invoiceId: number) => void;
  onSendEnd: () => void;
};

export function InvoiceEmailSendModal({
  open,
  onClose,
  invoice,
  emailTemplateDefaultModule,
  onSendStart,
  onSendEnd,
}: InvoiceEmailSendModalProps) {
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const { currentModelPermissions } = usePermissions();
  const canSend = Boolean(currentModelPermissions?.update);
  const recipients = useMemo(() => getInvoiceEmailRecipients(invoice), [invoice]);
  const contactIds = useMemo(() => getInvoiceEmailContactIds(invoice), [invoice]);
  const sendMutation = useSendInvoiceEmailMutation();
  const actionOptions = useMemo(() => getInvoiceEmailActionOptions(INVOICE_SCHEDULE_EMAIL_ENABLED), []);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);

  const form = useForm<InvoiceEmailSendFormSchema>({
    defaultValues: defaultFormValues(),
    resolver: zodResolver(invoiceEmailSendFormSchema),
  });

  const template = form.watch('template');
  const emailAction = form.watch('email_action');
  const scheduledAt = form.watch('scheduled_at');
  const isScheduleAction = emailAction === 'schedule';
  const scheduleSummary = useMemo(() => formatScheduledAtSummary(scheduledAt), [scheduledAt]);
  const deviceTimeZone = dateTime.getDeviceTimeZone();

  const companyName = nameResolver(invoice.client?.name, undefined, 'Company');
  const invoiceNumber = invoice.invoice_number?.trim() || '—';

  const recipientsMissingEmail = recipients.some((r) => !r.email);

  useEffect(() => {
    if (!open) {
      form.reset(defaultFormValues());
      setIsTemplateLoading(false);
      return;
    }

    form.reset(defaultFormValues());

    const cachedTemplates = getCachedInvoiceEmailTemplates('', 100);
    const cachedDefaults = getCachedEmailTemplateDefaults();
    const templatesQueryState = queryClient.getQueryState(invoiceEmailTemplatesQueryKey('', 100));
    const defaultsQueryState = queryClient.getQueryState(EMAIL_TEMPLATE_DEFAULTS_QUERY_KEY);
    const hasFreshCache =
      Boolean(cachedTemplates && cachedDefaults?.data) &&
      isQueryCacheFresh(templatesQueryState?.dataUpdatedAt, INVOICE_EMAIL_TEMPLATES_STALE_TIME) &&
      isQueryCacheFresh(defaultsQueryState?.dataUpdatedAt, EMAIL_TEMPLATE_DEFAULTS_STALE_TIME);

    if (hasFreshCache) {
      const defaultTemplate = resolveInvoiceEmailDefaultTemplate(
        emailTemplateDefaultModule,
        cachedDefaults!.data!,
        cachedTemplates!
      );
      form.setValue('template', defaultTemplate, { shouldValidate: true });
      setIsTemplateLoading(false);
      return;
    }

    setIsTemplateLoading(true);

    let cancelled = false;
    void Promise.all([fetchInvoiceEmailTemplates('', 100), fetchEmailTemplateDefaults()])
      .then(([templates, defaultsResponse]) => {
        if (cancelled) {
          return;
        }

        const defaultTemplate = resolveInvoiceEmailDefaultTemplate(
          emailTemplateDefaultModule,
          defaultsResponse.data ?? [],
          templates
        );

        form.setValue('template', defaultTemplate, { shouldValidate: true });
      })
      .finally(() => {
        if (!cancelled) {
          setIsTemplateLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, form, emailTemplateDefaultModule]);

  const handleSend = async (values: InvoiceEmailSendFormSchema) => {
    if (contactIds.length === 0) {
      snackbar.error('No billing or notification contacts are available on this invoice.');
      return;
    }
    if (!INVOICE_SEND_EMAIL_API_ENABLED) {
      snackbar.error('Sending invoice emails is temporarily unavailable.');
      return;
    }
    if (values.email_action === 'schedule' && !INVOICE_SCHEDULE_EMAIL_ENABLED) {
      snackbar.error('Scheduling invoice emails is temporarily unavailable.');
      return;
    }

    onSendStart(invoice.id);

    try {
      const response = await sendMutation.mutateAsync({
        invoiceId: invoice.id,
        email_template_id: values.template!.id,
        contact_ids: contactIds,
        ...(values.email_action === 'schedule' && values.scheduled_at
          ? { scheduled_at: dateTime.toUtcIsoString(values.scheduled_at) }
          : {}),
      });
      snackbar.success(getInvoiceEmailSendSuccessMessage(response, invoice.invoice_number));
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send invoice email.';
      snackbar.error(message);
    } finally {
      onSendEnd();
    }
  };

  const isPending = sendMutation.isPending;
  const sendDisabled = !canSend || isTemplateLoading || !template?.id || contactIds.length === 0 || isPending;
  const submitLabel = isScheduleAction ? 'Schedule email' : 'Send email';

  useEffect(() => {
    if (!open || isTemplateLoading || !template?.id || !canSend || contactIds.length === 0 || isPending) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      sendButtonRef.current?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [open, isTemplateLoading, template?.id, canSend, contactIds.length, isPending]);

  return (
    <Modal
      open={open}
      size="md"
      onClose={onClose}
      disableBackdropClosing={isPending}
      closeOnEscape={!isPending}
      className="overflow-visible"
    >
      <ModalHeader>Send invoice email</ModalHeader>

      <Form {...form} isSubmitting={isPending}>
        {({ handleSubmit }) => (
          <form onSubmit={stopPropagate(handleSubmit(handleSend))}>
            <ModalBody className="space-y-8">
              <div className="border-c-border-muted bg-c-background-muted rounded-lg border px-4 py-3 dark:bg-gray-900/50">
                <Text className="text-c-card-muted mb-3 text-xs font-semibold tracking-wide uppercase">
                  Sending for
                </Text>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                  <dt className="text-c-card-muted">Invoice</dt>
                  <dd className="text-c-card-foreground truncate font-medium">{invoiceNumber}</dd>
                  <dt className="text-c-card-muted">Company</dt>
                  <dd className="text-c-card-foreground truncate font-medium">{companyName}</dd>
                </dl>
              </div>

              <section>
                <Text as="h3" className="text-c-card-foreground mb-5 text-sm font-semibold">
                  Email
                </Text>
                <FormLayout className="mt-0">
                  <FormField
                    control={form.control}
                    name="template"
                    render={({ field: { value, onChange } }) => (
                      <InvoiceEmailTemplatesSelect
                        formItemClassName="col-span-12"
                        label="Template (required)"
                        value={value as EmailTemplateQueryLinkerSchema | null}
                        onValueChange={onChange}
                        loading={isTemplateLoading}
                        perPage={100}
                        disabled={!canSend || contactIds.length === 0 || isTemplateLoading}
                      />
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email_action"
                    render={({ field: { value, onChange } }) => (
                      <FormSelect
                        formItemClassName="col-span-12"
                        label="Action"
                        value={getInvoiceEmailActionOption(value)}
                        options={actionOptions}
                        getOptionLabel={(option) => option.name}
                        onChange={(option) => {
                          if (!option) {
                            return;
                          }

                          onChange(option.id);

                          if (option.id === 'schedule') {
                            form.setValue('scheduled_at', getDefaultScheduledAt(), {
                              shouldValidate: true,
                            });
                          } else {
                            form.setValue('scheduled_at', null, { shouldValidate: true });
                          }
                        }}
                        isClearable={false}
                        disabled={!canSend || contactIds.length === 0}
                      />
                    )}
                  />

                  {isScheduleAction ? (
                    <>
                      <FormField
                        control={form.control}
                        name="scheduled_at"
                        render={({ field: { value, onChange } }) => (
                          <FormDateTimeInput
                            formItemClassName="col-span-12"
                            label="Send on"
                            value={value}
                            onValueChange={onChange}
                            minDate={getScheduleMinDateTime()}
                            disabled={!canSend || contactIds.length === 0}
                          />
                        )}
                      />

                      <div className="col-span-12 -mt-6 space-y-1">
                        <HelperText>Times are in {deviceTimeZone}.</HelperText>
                        {scheduleSummary ? (
                          <Text className="text-c-card-muted text-sm">
                            This email will be sent on {scheduleSummary}.
                          </Text>
                        ) : null}
                      </div>
                    </>
                  ) : null}

                  <InvoiceEmailRecipientsList recipients={recipients} />

                  {recipientsMissingEmail ? (
                    <HelperText className="col-span-12 -mt-4">
                      One or more recipients have no email. Sending may still use contact IDs configured on the
                      agreement.
                    </HelperText>
                  ) : null}

                  {!canSend ? (
                    <Text className="text-c-card-muted col-span-12 text-sm">
                      You do not have permission to send invoice emails.
                    </Text>
                  ) : null}
                </FormLayout>
              </section>
            </ModalBody>

            <ModalFooter className="mt-6">
              <Button modal as="outline" type="button" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                ref={sendButtonRef}
                modal
                type="submit"
                showLoader={isPending}
                disabled={sendDisabled}
                className="focus:ring-primary-light focus:ring-2 focus:ring-offset-1"
              >
                {submitLabel}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
}
